// Exchange IDs can arrive as decimal strings or legacy scientific notation.
// Expand strings directly so large IDs never pass through floating-point math.
export function canonicalOrderId(value) {
  const id = String(value ?? "").trim();
  const match = /^(\d+)(?:\.(\d+))?(?:e\+?(\d+))?$/i.exec(id);
  if (!match) return id;
  const fraction = match[2] || "";
  const exponent = Number(match[3] || 0);
  if (exponent > 100) return id;
  const digits = match[1] + fraction;
  const split = match[1].length + exponent;
  if (split < digits.length && /[1-9]/.test(digits.slice(split))) return id;
  return (digits.slice(0, split) + "0".repeat(Math.max(0, split - digits.length))).replace(/^0+(?=\d)/, "");
}

const number = value => value == null || value === "" || !Number.isFinite(Number(value)) ? null : Number(value);
const millis = value => Number(value) < 1e12 ? Number(value) * 1000 : Number(value);
const quantity = item => Math.abs(number(item.executedQuantity ?? item.quantity) || 0);
const orderKey = (item, index) => JSON.stringify([
  item.accountId || "", String(item.symbol || "").toUpperCase(),
  canonicalOrderId(item.orderId) || `missing-${index}`
]);

export function tradeChartMarkers(journal) {
  const orders = new Map();
  // Executions are cumulative snapshots, not individual fills. Select the most
  // complete snapshot, preferring fill-backed PnL when quantities match.
  (journal.executions || []).forEach((item, index) => {
    const key = orderKey(item, `execution-${index}`);
    const previous = orders.get(key);
    const rank = candidate => [quantity(candidate), Number(candidate.fillsCount || 0), millis(candidate.updatedAt || candidate.createdAt || 0)];
    const incoming = rank(item), existing = previous ? rank(previous) : null;
    const firstDifference = existing ? incoming.findIndex((value, i) => value !== existing[i]) : -1;
    if (!previous || (firstDifference >= 0 && incoming[firstDifference] > existing[firstDifference])) orders.set(key, item);
  });

  const fills = new Map();
  (journal.fills || []).forEach((item, index) => {
    const key = orderKey(item, `fill-${index}`);
    const group = fills.get(key) || { ...item, quantity: 0, quoteQuantity: 0, realizedPnl: null, commission: null, netPnl: null, fillsCount: 0, updatedAt: 0 };
    const qty = quantity(item), price = number(item.price);
    if (!(qty > 0 && price > 0)) return;
    group.quantity += qty;
    group.quoteQuantity += number(item.quoteQuantity) ?? qty * price;
    for (const field of ["realizedPnl", "commission"]) {
      const value = number(item[field]);
      if (value != null) group[field] = (group[field] ?? 0) + (field === "commission" ? Math.abs(value) : value);
    }
    const pnl = number(item.netPnl) ?? (number(item.realizedPnl) == null ? null : Number(item.realizedPnl) - Math.abs(number(item.commission) || 0));
    if (pnl != null) group.netPnl = (group.netPnl ?? 0) + pnl;
    group.updatedAt = Math.max(group.updatedAt, millis(item.time || 0));
    group.fillsCount += 1;
    if (["REDUCE", "CLOSE"].includes(item.classification)) group.classification = "CLOSE";
    group.executedQuantity = group.quantity;
    group.avgPrice = group.quoteQuantity / group.quantity;
    fills.set(key, group);
  });
  for (const [key, group] of fills) {
    const execution = orders.get(key);
    // Never add a raw-fill total to a cumulative execution snapshot. A bounded
    // fill history may cover only part of an order, so retain fuller summaries.
    if (!execution || (group.quantity >= quantity(execution) * (1 - 1e-10)
      && group.fillsCount >= Number(execution.fillsCount || 0))) orders.set(key, group);
  }
  return Array.from(orders, ([key, item]) => {
    const qty = quantity(item), quote = number(item.quoteQuantity);
    const price = qty > 0 && quote > 0 ? quote / qty : number(item.avgPrice) || number(item.price);
    return { ...item, markerKey: key, markerTime: item.updatedAt || item.time || item.createdAt, markerPrice: price, avgPrice: price };
  });
}

export function tradeCloseLabel(fill, side, trade) {
  const entry = number(trade?.actualEntry || trade?.plannedEntry);
  const price = number(fill?.avgPrice ?? fill?.price);
  const leverage = number(trade?.leverage);
  const pnl = number(fill?.netPnl ?? fill?.realizedPnl);
  const parts = [];
  if (entry > 0 && price > 0 && leverage > 0) {
    const pct = (price - entry) / entry * 100 * leverage * (side === "SHORT" ? -1 : 1);
    parts.push(`${pct >= 0 ? "+" : ""}${pct.toFixed(1)}%`);
  }
  if (pnl != null) parts.push(`${pnl < 0 ? "-" : "+"}$${Math.abs(pnl).toFixed(2)}`);
  return parts.join(", ");
}
